const providerLabels = {
  elevenlabs: 'ElevenLabs Scribe',
  'deepgram-nova3': 'Deepgram Nova-3',
  'deepgram-flux': 'Deepgram Flux',
  assemblyai: 'AssemblyAI Universal-Streaming',
  soniox: 'Soniox Realtime v5',
  cartesia: 'Cartesia Ink-Whisper',
};

const text = (tag, value, className) => {
  const node = document.createElement(tag);
  node.textContent = value;
  if (className) node.className = className;
  return node;
};

// Turbolinks 5 restores a cached copy of the body on a Back visit: the data
// attributes stamped on it survive, the listeners do not. Track the nodes we
// bound in a WeakSet instead. It is empty on every page load, so a restored
// node counts as new and gets its listeners back.
const boundNodes = new WeakSet();
const bindOnce = node => {
  if (!node || boundNodes.has(node)) return false;
  boundNodes.add(node);
  return true;
};

// A missing, blank or unparseable number means "we do not know", never zero.
const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// The server-rendered table writes "Not scored" for a row with no word error
// rate (app/views/account/call_playgrounds/call_results.html.erb). An unscored
// row must read the same here, and must not be counted as a perfect 0.0% and
// marked best.
const NOT_SCORED = 'Not scored';
const scoredWer = result => (result && result.status === 'complete' ? numberOrNull(result.wer) : null);
const bestScoredWer = results => {
  const scores = (results || []).map(scoredWer).filter(value => value !== null);
  return scores.length ? Math.min(...scores) : null;
};
const werCellText = (result, bestWer) => {
  const wer = scoredWer(result);
  if (wer === null) return NOT_SCORED;
  return `${(wer * 100).toFixed(1)}%${wer === bestWer ? ' · best' : ''}`;
};

const renderResults = (root, job) => {
  const output = root.querySelector('[data-benchmark-results]');
  output.innerHTML = '';
  output.hidden = false;
  output.appendChild(text('h3', 'Results for this recording', 'stt-setup__h2'));

  const complete = (job.results || []).filter(result => result.status === 'complete');
  if (!complete.length) {
    output.appendChild(text('p', job.error || 'No engine returned a result.', 'stt-setup__note'));
    return;
  }
  const bestWer = bestScoredWer(complete);
  const table = document.createElement('table');
  table.className = 'stt-results-table';
  const head = document.createElement('thead');
  const headerRow = document.createElement('tr');
  ['Engine', 'Transcript', 'Word error rate', 'Final after speech', 'Cost', 'LiveKit run'].forEach(label => {
    headerRow.appendChild(text('th', label));
  });
  head.appendChild(headerRow);
  table.appendChild(head);
  const body = document.createElement('tbody');

  (job.results || []).forEach(result => {
    const row = document.createElement('tr');
    if (result.status !== 'complete') {
      row.appendChild(text('td', providerLabels[result.provider] || result.provider));
      const error = text('td', result.error || 'Engine failed');
      error.colSpan = 5;
      row.appendChild(error);
    } else {
      const showRunNumber = Number(job.repeats) > 1;
      const label = `${providerLabels[result.provider] || result.provider}${showRunNumber ? ` · run ${result.repetition}` : ''}`;
      row.appendChild(text('td', label));
      row.appendChild(text('td', result.transcript));
      row.appendChild(text('td', werCellText(result, bestWer)));
      row.appendChild(text('td', result.final_delay_ms == null ? 'Not reported' : `${result.final_delay_ms} ms`));
      row.appendChild(text('td', result.estimated_cost_usd == null ? 'Not priced' : `$${Number(result.estimated_cost_usd).toFixed(5)}`));
      row.appendChild(text('td', result.room_name || 'Not reported'));
    }
    body.appendChild(row);
  });
  table.appendChild(body);
  const wrapper = document.createElement('div');
  wrapper.className = 'stt-results-table-w mt-3';
  wrapper.appendChild(table);
  output.appendChild(wrapper);
  output.appendChild(text(
    'p',
    'Every engine heard this same recording through LiveKit. Word error rate is measured against your reference text above; accent marks and spelling count as errors, punctuation does not. Lower is better.',
    'stt-setup__note'
  ));
};

// One page, four states: idle, recording, comparing, done. Every control that
// feeds a comparison is locked while one runs, so a stray click cannot change
// the recording or the engine set mid-job, and everything unlocks together.
const setComparing = (root, comparing) => {
  const run = root.querySelector('[data-run-benchmark]');
  const record = root.querySelector('[data-record]');
  root.querySelector('[data-reference]').disabled = comparing;
  root.querySelector('[data-benchmark-repeats]').disabled = comparing;
  root.querySelectorAll('[data-benchmark-provider]').forEach(input => {
    if (input.dataset.unavailable === 'true') return;
    input.disabled = comparing;
  });
  record.disabled = comparing || record.dataset.serviceDown === 'true';
  run.disabled = comparing;
  run.textContent = comparing
    ? 'Comparing…'
    : (root.querySelector('[data-benchmark-results]').hidden ? 'Compare this recording' : 'Compare again');
  root.classList.toggle('stt-benchmark--comparing', comparing);
};

// Rails holds the job (the sidecar keeps nothing) and reports it as queued,
// running, complete or error — or stale, when a running job has not been
// updated for longer than the job timeout. Anything else — a status it grows
// later, a proxy error page, an empty body — is treated as the end of the job
// rather than polled forever, and the wait is capped so a job that stalls in
// "running" still lets go.
const POLL_INTERVAL_MS = 1000;
const MAX_POLLS = 1800; // 30 minutes at one poll a second
const PROGRESS_STATUSES = ['queued', 'running'];
const STALE_STATUS = 'stale';

const progressText = job => {
  const current = job && job.current && job.current.provider
    ? ` Running ${providerLabels[job.current.provider] || job.current.provider}.`
    : '';
  const completed = numberOrNull(job && job.completed);
  const total = numberOrNull(job && job.total);
  const counts = completed === null || total === null
    ? 'Comparing engines…'
    : `${completed} of ${total} engine runs finished.`;
  return `${counts}${current}`;
};

// Pure decision for one poll response: keep waiting, or stop with a message and
// possibly a results table.
const pollStep = (job, polls) => {
  const status = job && job.status;
  if (status === 'complete') {
    const total = numberOrNull(job.total);
    return {
      done: true,
      render: true,
      message: total === null ? 'Done: the comparison finished.' : `Done: ${total} engine runs finished.`,
    };
  }
  if (status === 'error') {
    return {
      done: true,
      render: true,
      message: (job && job.error) || 'Finished with engine errors. See the results below.',
    };
  }
  if (status === STALE_STATUS) {
    // Rails stopped hearing from the sidecar. Show whatever rows landed, then
    // stop: polling cannot revive a job the sidecar no longer holds.
    return {
      done: true,
      render: Boolean(job && Array.isArray(job.results) && job.results.length),
      message: (job && job.error) || 'Stopped waiting: the comparison stopped reporting progress.',
    };
  }
  if (!PROGRESS_STATUSES.includes(status)) {
    return {
      done: true,
      render: Boolean(job && Array.isArray(job.results) && job.results.length),
      message: status
        ? `Stopped waiting: the comparison reported an unexpected status (${status}).`
        : 'Stopped waiting: the comparison did not report its progress.',
    };
  }
  if (polls >= MAX_POLLS) {
    return {
      done: true,
      render: false,
      message: 'Stopped waiting after 30 minutes. The comparison may still be running — reload this page to check.',
    };
  }
  return { done: false, render: false, message: progressText(job) };
};

const poll = async (root, url) => {
  const status = root.querySelector('[data-compare-status]');
  status.hidden = false;
  let polls = 0;
  while (true) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const job = await response.json();
    if (!response.ok) throw new Error((job && job.error) || 'Could not read benchmark progress.');
    polls += 1;
    const step = pollStep(job, polls);
    status.textContent = step.message;
    if (step.done) {
      if (step.render) {
        renderResults(root, job);
        root.querySelector('[data-benchmark-results]').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

const initialize = root => {
  if (!bindOnce(root)) return;
  const record = root.querySelector('[data-record]');
  record.dataset.serviceDown = record.disabled ? 'true' : 'false';
  const stop = root.querySelector('[data-stop]');
  const run = root.querySelector('[data-run-benchmark]');
  const playback = root.querySelector('[data-playback]');
  const status = root.querySelector('[data-benchmark-status]');
  const consent = root.querySelector('[data-recording-consent]');
  let recordingConsentId;
  let recorder;
  let stream;
  let chunks = [];
  let recording;
  let timer;
  let startedAt;

  root.querySelectorAll('[data-benchmark-provider]').forEach(input => {
    input.addEventListener('change', () => {
      input.closest('.stt-provider').classList.toggle('stt-provider--on', input.checked);
    });
  });

  record.addEventListener('click', async () => {
    if (!consent.checked) {
      status.textContent = 'Please consent to recording your own voice before recording.';
      return;
    }
    record.disabled = true;
    try {
      // Persist the speaker's acknowledgement before even opening the mic.
      const consentResponse = await fetch(root.dataset.consentUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ recording_consent: '1', purpose: 'controlled' }),
      });
      const acknowledgement = await consentResponse.json();
      if (!consentResponse.ok) throw new Error(acknowledgement.error || 'Could not save recording consent.');
      recordingConsentId = acknowledgement.recording_consent_id;
      recording = null;
      run.disabled = true;
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ['audio/webm;codecs=opus', 'audio/webm'].find(type => MediaRecorder.isTypeSupported(type));
      recorder = new MediaRecorder(stream, preferred ? { mimeType: preferred } : undefined);
      chunks = [];
      recorder.addEventListener('dataavailable', event => { if (event.data.size) chunks.push(event.data); });
      recorder.addEventListener('stop', () => {
        recording = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        playback.src = URL.createObjectURL(recording);
        playback.hidden = false;
        run.disabled = false;
        root.querySelector('[data-compare-hint]').textContent = 'Same audio in every room';
        status.textContent = 'Recording ready. Type exactly what you said, then compare.';
      });
      recorder.start(250);
      consent.checked = false;
      startedAt = Date.now();
      status.textContent = 'Recording… speak one natural sentence, then stop.';
      record.disabled = true;
      stop.disabled = false;
      timer = setInterval(() => {
        status.textContent = `Recording… ${Math.floor((Date.now() - startedAt) / 1000)}s`;
      }, 500);
    } catch (error) {
      if (stream) stream.getTracks().forEach(track => track.stop());
      status.textContent = `Microphone unavailable: ${error.message}`;
      record.disabled = false;
    }
  });

  stop.addEventListener('click', () => {
    clearInterval(timer);
    recorder.stop();
    stream.getTracks().forEach(track => track.stop());
    record.disabled = false;
    stop.disabled = true;
  });

  run.addEventListener('click', async () => {
    const providers = [...root.querySelectorAll('[data-benchmark-provider]:checked')].map(input => input.value);
    const reference = root.querySelector('[data-reference]').value.trim();
    if (!recording || !reference || providers.length < 2) {
      status.textContent = 'Record a sample, enter exactly what you said, and choose at least two engines.';
      return;
    }
    setComparing(root, true);
    const compareStatus = root.querySelector('[data-compare-status]');
    compareStatus.hidden = false;
    compareStatus.textContent = 'Uploading the recording…';
    const form = new FormData();
    form.append('audio', recording, 'learner-sample.webm');
    form.append('recording_consent_id', recordingConsentId);
    providers.forEach(provider => form.append('stt_providers[]', provider));
    form.append('reference', reference);
    form.append('repeats', root.querySelector('[data-benchmark-repeats]').value);
    try {
      const response = await fetch(root.dataset.createUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content, Accept: 'application/json' },
        body: form,
      });
      const job = await response.json();
      if (!response.ok) throw new Error(job.error || 'Could not start the comparison.');
      await poll(root, job.status_url);
    } catch (error) {
      compareStatus.hidden = false;
      compareStatus.textContent = error.message;
    } finally {
      setComparing(root, false);
    }
  });
};

// Per-turn playback on the confirm table: the recording is one file, so a
// turn is played by seeking the page's player to the turn's offset and
// pausing at its end. Nothing is cut server-side.
const TURN_PLAY_LEAD_SECONDS = 0.25;
const TURN_PLAY_TAIL_SECONDS = 0.4;
const initializeTurnPlayback = () => {
  const audio = document.querySelector('.stt-call-audio audio');
  if (!audio) return;
  let stopAt = null;
  let activeButton = null;
  const reset = () => {
    if (activeButton) activeButton.classList.remove('stt-confirm__play--on');
    activeButton = null;
    stopAt = null;
  };
  // The waveform strip drives the same element. When someone scrubs, this
  // turn's stop time is no longer what they asked for, so the strip clears it
  // through here rather than either file knowing the other's internals.
  audio.sttClearTurnStop = reset;
  if (bindOnce(audio)) {
    audio.addEventListener('timeupdate', () => {
      if (stopAt !== null && audio.currentTime >= stopAt) {
        audio.pause();
        reset();
      }
    });
    audio.addEventListener('pause', () => { if (stopAt !== null && audio.currentTime < stopAt) reset(); });
  }
  document.querySelectorAll('[data-play-start]').forEach(button => {
    if (!bindOnce(button)) return;
    button.addEventListener('click', () => {
      const start = Math.max(0, parseFloat(button.dataset.playStart) - TURN_PLAY_LEAD_SECONDS);
      const end = parseFloat(button.dataset.playEnd) + TURN_PLAY_TAIL_SECONDS;
      if (activeButton === button) {
        audio.pause();
        reset();
        return;
      }
      reset();
      activeButton = button;
      button.classList.add('stt-confirm__play--on');
      stopAt = end;
      audio.currentTime = start;
      audio.play().catch(() => reset());
    });
  });
};

const initializeAll = () => document.querySelectorAll('[data-stt-benchmark]').forEach(initialize);
// The server always replays the live engine as well (CallPlaygroundsController
// adds it to comparison_providers), so its checkbox in step 2 is shown as
// checked and locked with a note rather than left as a choice that changes
// nothing.
// The drift check replays the recording through the live engine. A
// speech-to-speech model cannot be replayed at all — it hears the learner
// itself and exposes no transcription endpoint — so the box is locked off
// rather than left as a promise of a run that could only fail after waiting
// the length of the call. CallPlayground.store_call_replay stores false for it
// either way. The attribute is a comma-separated list: there are two such
// engines now (openai-realtime and openai-live), and an equality test against
// one of them silently let the other through.
const syncDriftCheck = live => {
  const box = document.querySelector('[data-drift-check]');
  if (!box) return;
  const blocked = String(box.dataset.unavailableFor || '').split(',').filter(Boolean);
  const impossible = Boolean(live) && blocked.includes(live);
  if (impossible) {
    box.checked = false;
    box.disabled = true;
  } else {
    box.disabled = false;
  }
  box.closest('.stt-compare-option')?.classList.toggle('opacity-50', impossible);
  const note = document.querySelector('[data-drift-unavailable]');
  if (note) note.hidden = !impossible;
};

const syncLiveEngineInComparison = () => {
  const live = document.querySelector('input[name="live_provider"]:checked')?.value;
  syncDriftCheck(live);
  document.querySelectorAll('input[name="comparison_providers[]"]').forEach(input => {
    const isLive = input.value === live;
    const note = input.closest('.stt-compare-option')?.querySelector('.stt-compare-option__live');
    if (isLive) {
      input.dataset.wasChecked = input.dataset.wasChecked ?? String(input.checked);
      input.checked = true;
      input.disabled = true;
      input.dataset.lockedAsLive = 'true';
    } else if (input.dataset.lockedAsLive === 'true') {
      delete input.dataset.lockedAsLive;
      input.disabled = input.dataset.unavailable === 'true';
      input.checked = input.dataset.wasChecked === 'true';
      delete input.dataset.wasChecked;
    }
    if (note) note.hidden = !isLive;
  });
};

const initializeLiveChoices = () => {
  document.querySelectorAll('input[name="live_provider"]').forEach(input => {
    if (!bindOnce(input)) return;
    input.addEventListener('change', () => {
      document.querySelectorAll('input[name="live_provider"]').forEach(choice => {
        choice.closest('.stt-provider')?.classList.toggle('stt-provider--on', choice.checked);
      });
      syncLiveEngineInComparison();
    });
  });
  // Remember which boxes the server disabled (engine cannot do the language)
  // before any locking, and only on the first pass: a second initialise must
  // not read a live-locked box as unavailable.
  document.querySelectorAll('input[name="comparison_providers[]"]').forEach(input => {
    if (input.dataset.availabilityRead) return;
    input.dataset.availabilityRead = 'true';
    if (input.disabled) input.dataset.unavailable = 'true';
  });
  syncLiveEngineInComparison();

  const toggle = document.querySelector('input[name="auto_compare"]');
  if (bindOnce(toggle)) {
    const sync = () => {
      document.querySelectorAll('input[name="comparison_providers[]"]').forEach(input => {
        input.disabled = !toggle.checked || input.dataset.lockedAsLive === 'true' || input.dataset.unavailable === 'true';
        input.closest('.stt-compare-option')?.classList.toggle('opacity-50', !toggle.checked);
      });
    };
    toggle.addEventListener('change', sync);
    sync();
  }
};
// Has a human vouched for one row?
//
// Not "the editor has text in it": every editor is pre-filled with the live
// engine's own words, so that read every row as confirmed before anyone had
// looked at one, and the word error rates that followed measured how closely
// each engine agreed with the live engine rather than with the learner. A row
// counts when the text differs from what was pre-filled, or when the reviewer
// ticked "correct as is". Same rule as the server's, deliberately.
const normalizeTurnText = value => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
const rowConfirmed = state => {
  if (!state) return false;
  if (state.checked) return true;
  const text = normalizeTurnText(state.text);
  return text.length > 0 && text !== normalizeTurnText(state.originalText);
};
const confirmedTurnCount = states => (states || []).filter(rowConfirmed).length;

// The confirm-the-transcript table. Every row keeps its text in a hidden field
// so the form still submits without any of this, and the rows are never
// reordered: the hidden fields submit in DOM order and the server joins them in
// that order.
const initializeReferenceTable = () => {
  const root = document.querySelector('[data-turn-table]');
  if (!bindOnce(root)) return;

  const rows = [...root.querySelectorAll('[data-turn-row]')];
  const spoken = rows.filter(row => row.dataset.turnSilent !== 'true');
  const progress = root.querySelector('[data-turn-progress]');
  const referenceField = root.querySelector('[data-turn-reference]');
  const block = root.querySelector('[data-turn-block]');
  const blockText = root.querySelector('[data-turn-block-text]');
  const blockToggle = root.querySelector('[data-turn-block-toggle]');
  const submit = root.querySelector('[data-turn-submit]');
  const gate = root.querySelector('[data-turn-gate]');
  const confirmAll = root.querySelector('[data-turn-confirm-all]');

  const rowText = row => row.querySelector('[data-turn-editor]').innerText.replace(/\s+/g, ' ').trim();
  const rowCheckbox = row => row.querySelector('[data-turn-confirm]');
  const rowState = row => ({
    text: rowText(row),
    originalText: row.dataset.turnText || '',
    checked: Boolean(rowCheckbox(row) && rowCheckbox(row).checked),
  });

  const syncRow = row => {
    const value = rowText(row);
    row.querySelector('[data-turn-value]').value = value;
    return value;
  };

  const syncAll = () => {
    rows.forEach(syncRow);
    // The reference is the spoken turns only: a silent turn has nothing to
    // score and an empty line in the middle of it helps nobody.
    referenceField.value = spoken
      .map(row => rowText(row) || row.dataset.turnText || '')
      .filter(value => value.length)
      .join('\n');
    const confirmed = confirmedTurnCount(spoken.map(rowState));
    progress.textContent = `${confirmed} of ${spoken.length} turns confirmed`;
    const remaining = spoken.length - confirmed;
    if (submit) submit.disabled = remaining > 0;
    if (gate) gate.hidden = remaining === 0;
    root.classList.toggle('stt-confirm--ready', remaining === 0);
  };

  rows.forEach(row => {
    row.querySelector('[data-turn-editor]').addEventListener('input', syncAll);
    const checkbox = rowCheckbox(row);
    if (checkbox) checkbox.addEventListener('change', syncAll);
    row.querySelectorAll('[data-turn-use]').forEach(button => {
      button.addEventListener('click', () => {
        row.querySelector('[data-turn-editor]').textContent = button.dataset.turnText;
        // Taking an engine's words IS the review. Ticking the box as well
        // matters when the words taken are the live engine's own, where the
        // text alone cannot tell the click from the form as it was rendered.
        if (checkbox) checkbox.checked = true;
        syncAll();
      });
    });
  });

  // Listening to thirty turns and agreeing with all of them must not cost
  // thirty clicks. Rows already edited are left alone: they are confirmed by
  // their text, and ticking them would say nothing extra.
  if (confirmAll) {
    confirmAll.addEventListener('click', () => {
      spoken.forEach(row => {
        const checkbox = rowCheckbox(row);
        if (checkbox && !rowConfirmed(rowState(row))) checkbox.checked = true;
      });
      syncAll();
    });
  }

  root.querySelectorAll('[data-turn-filter]').forEach(button => {
    button.addEventListener('click', () => {
      const filter = button.dataset.turnFilter;
      root.querySelectorAll('[data-turn-filter]').forEach(other => {
        const on = other === button;
        other.classList.toggle('stt-confirm__filter--on', on);
        other.setAttribute('aria-pressed', String(on));
      });
      rows.forEach(row => {
        const silent = row.dataset.turnSilent === 'true';
        if (filter === 'flagged') row.hidden = row.dataset.turnFlagged !== 'true';
        else if (filter === 'disagreement') row.hidden = row.dataset.turnDisagreement !== 'true';
        else row.hidden = silent && root.dataset.silentShown !== 'true';
      });
      const summary = root.querySelector('[data-turn-silent-summary]');
      if (summary) summary.hidden = filter !== 'all';
    });
  });

  const silentToggle = root.querySelector('[data-turn-silent-toggle]');
  if (silentToggle) {
    silentToggle.addEventListener('click', () => {
      const shown = root.dataset.silentShown === 'true';
      root.dataset.silentShown = String(!shown);
      rows.filter(row => row.dataset.turnSilent === 'true').forEach(row => { row.hidden = shown; });
      silentToggle.textContent = shown ? 'show' : 'hide';
    });
  }

  // One text block and the rows are the same data: opening fills the block from
  // the rows, closing splits it back line by line in the same order.
  if (blockToggle) {
    blockToggle.addEventListener('click', () => {
      if (block.hidden) {
        blockText.value = rows.map(rowText).join('\n');
        block.hidden = false;
        blockToggle.textContent = 'back to the table';
        return;
      }
      const lines = blockText.value.split('\n');
      rows.forEach((row, position) => {
        row.querySelector('[data-turn-editor]').textContent = (lines[position] || '').trim();
      });
      block.hidden = true;
      blockToggle.textContent = 'edit as one text block';
      syncAll();
    });
  }

  syncAll();
};

const initializePage = () => {
  initializeTurnPlayback();
  initializeAll();
  initializeLiveChoices();
  initializeReferenceTable();
};
document.addEventListener('DOMContentLoaded', initializePage);
document.addEventListener('turbolinks:load', initializePage);

// The pure helpers above are exercised by test/javascript/call_playground_benchmark_test.js
// with plain Node and no DOM. The page itself requires this file only for its
// side effects (app/javascript/account/index.js), so the export is inert there.
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    initialize,
    bindOnce,
    rowConfirmed,
    confirmedTurnCount,
    numberOrNull,
    bestScoredWer,
    werCellText,
    progressText,
    pollStep,
    NOT_SCORED,
    POLL_INTERVAL_MS,
    MAX_POLLS,
    PROGRESS_STATUSES,
    STALE_STATUS,
  };
}
